'use client';

import { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/hooks/use-user';
import { useReactNativeBridge } from '@/hooks/useReactNativeBridge';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Shield,
  Bell,
  Trash2,
  Loader2,
  Smartphone,
  Mail,
  ArrowLeft,
  KeyRound,
  CheckCircle2,
  ListChecks,
} from 'lucide-react';
import { OnboardingMembershipAutosaveForm } from '@/components/onboarding/onboarding-membership-autosave-form';
import { GivesAsksCommunitySection } from '@/components/profile/gives-asks-community-section';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { AccountHeader } from '@/components/layouts/AccountHeader';

interface DeviceNotification {
  id: string;
  deviceType: string;
  notificationsEnabled: boolean;
}

interface NotificationPreferences {
  userPreferences: {
    emailEnabled: boolean;
    marketingEnabled: boolean;
  };
  devices: DeviceNotification[];
}

interface DeleteRequest {
  id: string;
  status: string;
  reason: string;
  createdAt: string;
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

export default function UserSettingsPage() {
  const { user, isLoading: userLoading } = useUser();
  const navigate = useNavigate();
  const { isNativeApp } = useReactNativeBridge();

  const { data: notificationData, mutate: mutateNotifications } = useSWR<NotificationPreferences>(
    '/api/user/notifications',
    fetcher,
    { revalidateOnFocus: false }
  );

  const { data: deleteRequestData, mutate: mutateDeleteRequest } = useSWR<{
    deleteRequest: DeleteRequest;
  }>('/api/user/delete-request', fetcher, { revalidateOnFocus: false });

  const deleteRequest = deleteRequestData?.deleteRequest;

  const communitiesWithOnboarding = useMemo(
    () =>
      (user?.communities ?? []).filter((c) =>
        Boolean(c.onboardingForm?.steps && c.onboardingForm.steps.length > 0)
      ),
    [user]
  );

  const communitiesWithGivesAsks = useMemo(
    () => (user?.communities ?? []).filter((c) => Boolean(c.isGivesAsksEnabled)),
    [user]
  );

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const [otpMode, setOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [notificationsSaving, setNotificationsSaving] = useState(false);

  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);

  useEffect(() => {
    if (notificationData) {
      setEmailEnabled(notificationData.userPreferences.emailEnabled);
      setMarketingEnabled(notificationData.userPreferences.marketingEnabled);
    }
  }, [notificationData]);

  const handleSendOtp = async () => {
    setOtpSending(true);
    setOtpVerified(false);
    try {
      const res = await axiosInstance.post('/api/user/password/otp');
      setMaskedPhone(res.data.maskedPhone || '');
      setOtpSent(true);
      toast.success('OTP sent to your registered phone');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async (otp: string) => {
    if (otp.length !== 6) return;
    setOtpVerifying(true);
    try {
      await axiosInstance.post('/api/user/password/otp/verify', { otp });
      setOtpVerified(true);
      toast.success('OTP verified');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Invalid OTP');
      setOtpVerified(false);
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPasswordError('');
    setPasswordSaving(true);

    try {
      const payload = otpMode ? { otp: otpValue, newPassword } : { currentPassword, newPassword };

      await axiosInstance.patch('/api/user/password', payload).then((res) => {
        if (res.data?.token) {
          localStorage.setItem('token', res.data.token);
        }
      });
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOtpMode(false);
      setOtpSent(false);
      setOtpValue('');
      setOtpVerified(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to update password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleUpdateEmailNotifications = async (enabled: boolean) => {
    setEmailEnabled(enabled);
    setNotificationsSaving(true);

    try {
      await axiosInstance.patch('/api/user/notifications', {
        emailEnabled: enabled,
      });
      mutateNotifications();
      toast.success('Email notifications updated');
    } catch {
      setEmailEnabled(!enabled);
      toast.error('Failed to update email notifications');
    } finally {
      setNotificationsSaving(false);
    }
  };

  const handleUpdatePushNotifications = async (enabled: boolean) => {
    const currentDevice = notificationData?.devices?.[0];
    setNotificationsSaving(true);

    try {
      await axiosInstance.patch('/api/user/notifications', {
        notificationsEnabled: enabled,
        deviceId: currentDevice?.id,
      });
      mutateNotifications();
      toast.success(enabled ? 'Push notifications enabled' : 'Push notifications disabled');
    } catch {
      toast.error('Failed to update push notifications');
    } finally {
      setNotificationsSaving(false);
    }
  };

  const handleUpdateMarketingNotifications = async (enabled: boolean) => {
    setMarketingEnabled(enabled);
    setNotificationsSaving(true);

    try {
      await axiosInstance.patch('/api/user/notifications', {
        marketingEnabled: enabled,
      });
      mutateNotifications();
      toast.success('Marketing emails updated');
    } catch {
      setMarketingEnabled(!enabled);
      toast.error('Failed to update marketing emails');
    } finally {
      setNotificationsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteSaving(true);

    try {
      await axiosInstance.post('/api/user/delete-request', {
        reason: deleteReason,
      });
      toast.success('Delete request submitted successfully');
      mutateDeleteRequest();
      setDeleteReason('');
      setDeleteConfirmation('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to submit delete request');
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleCancelDeleteRequest = async () => {
    try {
      await axiosInstance.delete('/api/user/delete-request');
      toast.success('Delete request cancelled');
      mutateDeleteRequest();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to cancel delete request');
    }
  };

  if (userLoading && !user) {
    return (
      <div className="container mx-auto py-10 px-4">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto py-10 px-4">
        <div className="text-center text-muted-foreground">User not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AccountHeader user={user} />
      <main className="flex-1 overflow-y-auto w-full">
        <div className="container mx-auto py-10 px-4 max-w-4xl">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Account Settings</h1>
            <p className="text-muted-foreground mt-2">
              Manage your security and notification preferences.
            </p>
          </div>

          <SegmentedTabs defaultValue="security" className="space-y-6">
            <SegmentedTabsList variant="compact" className="w-full sm:w-fit">
              <SegmentedTabsTrigger variant="compact" value="security">
                Security
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="compact" value="notifications">
                Notifications
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="compact" value="additional">
                Additional details
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="compact" value="danger">
                Danger Zone
              </SegmentedTabsTrigger>
            </SegmentedTabsList>

            {/* Security Tab */}
            <SegmentedTabsContent value="security" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Change Password
                  </CardTitle>
                  <CardDescription>
                    Update your password to keep your account secure.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!otpMode ? (
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setOtpMode(true);
                          setCurrentPassword('');
                        }}
                        className="text-xs text-primary font-medium hover:underline"
                      >
                        Forgot password? Verify via phone OTP
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <KeyRound className="h-4 w-4 text-primary" />
                          Verify via Phone OTP
                        </Label>
                        <button
                          type="button"
                          onClick={() => {
                            setOtpMode(false);
                            setOtpSent(false);
                            setOtpValue('');
                            setOtpVerified(false);
                          }}
                          className="text-xs font-medium text-primary border border-border rounded-lg px-3 py-1.5 bg-background hover:bg-muted transition-colors"
                        >
                          Use password instead
                        </button>
                      </div>

                      {!otpSent ? (
                        <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Smartphone className="h-4 w-4 shrink-0" />
                            <span>
                              An OTP will be sent to your registered phone number via WhatsApp.
                            </span>
                          </div>
                          <Button
                            onClick={handleSendOtp}
                            disabled={otpSending}
                            variant="outline"
                            className="w-full"
                          >
                            {otpSending ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Sending OTP...
                              </>
                            ) : (
                              <>
                                <Smartphone className="h-4 w-4 mr-2" />
                                Send OTP
                              </>
                            )}
                          </Button>
                        </div>
                      ) : otpVerified ? (
                        <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                            <span className="text-foreground font-medium">
                              Phone verified via OTP
                              {maskedPhone ? (
                                <>
                                  {' — '}
                                  <span className="font-mono">{maskedPhone}</span>
                                </>
                              ) : null}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setOtpVerified(false);
                              setOtpValue('');
                              handleSendOtp();
                            }}
                            disabled={otpSending}
                            className="text-xs text-primary font-medium hover:underline disabled:opacity-50"
                          >
                            {otpSending ? 'Resending...' : 'Resend OTP'}
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Smartphone className="h-4 w-4 text-primary shrink-0" />
                            <span className="text-foreground font-medium">
                              OTP sent to{' '}
                              {maskedPhone ? (
                                <span className="font-mono">{maskedPhone}</span>
                              ) : (
                                'your phone'
                              )}
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            <Label>Enter OTP</Label>
                            <div className="flex justify-center">
                              <InputOTP
                                maxLength={6}
                                value={otpValue}
                                onChange={(val) => {
                                  setOtpValue(val);
                                  if (val.length === 6 && !otpVerified) {
                                    handleVerifyOtp(val);
                                  }
                                }}
                              >
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                            </div>
                          </div>
                          {otpVerifying && (
                            <p className="text-xs text-primary font-medium flex items-center gap-1.5">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Verifying...
                            </p>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setOtpValue('');
                              handleSendOtp();
                            }}
                            disabled={otpSending || otpVerifying}
                            className="text-xs text-primary font-medium hover:underline disabled:opacity-50"
                          >
                            {otpSending ? 'Resending...' : 'Resend OTP'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    <p className="text-xs text-muted-foreground">
                      Password must be at least 8 characters long.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                  {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleChangePassword}
                      disabled={
                        passwordSaving ||
                        (!otpMode && !currentPassword) ||
                        (otpMode && !otpVerified) ||
                        !newPassword ||
                        newPassword !== confirmPassword
                      }
                    >
                      {passwordSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Updating...
                        </>
                      ) : (
                        'Update Password'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </SegmentedTabsContent>

            {/* Notifications Tab */}
            <SegmentedTabsContent value="notifications" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5" />
                    Notification Preferences
                  </CardTitle>
                  <CardDescription>Choose how you want to receive notifications.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="emailNotifications" className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email Notifications
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Receive notifications via email.
                      </p>
                    </div>
                    <Switch
                      id="emailNotifications"
                      checked={emailEnabled}
                      onCheckedChange={handleUpdateEmailNotifications}
                      disabled={notificationsSaving}
                    />
                  </div>

                  {isNativeApp && (
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="pushNotifications" className="flex items-center gap-2">
                          <Smartphone className="h-4 w-4" />
                          Push Notifications
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Receive push notifications on your current device.
                        </p>
                      </div>
                      <Switch
                        id="pushNotifications"
                        checked={notificationData?.devices?.[0]?.notificationsEnabled ?? true}
                        onCheckedChange={handleUpdatePushNotifications}
                        disabled={notificationsSaving}
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="marketingEmails">Marketing Emails</Label>
                      <p className="text-xs text-muted-foreground">
                        Receive emails about new features and updates.
                      </p>
                    </div>
                    <Switch
                      id="marketingEmails"
                      checked={marketingEnabled}
                      onCheckedChange={handleUpdateMarketingNotifications}
                      disabled={notificationsSaving}
                    />
                  </div>
                </CardContent>
              </Card>
            </SegmentedTabsContent>

            {/* Additional Details Tab */}
            <SegmentedTabsContent value="additional" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ListChecks className="h-5 w-5" />
                    Additional Details
                  </CardTitle>
                  <CardDescription>
                    Community onboarding answers and gives &amp; asks.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-8">
                    {communitiesWithOnboarding.length === 0 &&
                    communitiesWithGivesAsks.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        You are not in any communities with onboarding questions. When you join one,
                        you can edit those answers here.
                      </p>
                    ) : (
                      <>
                        {communitiesWithOnboarding.map((c) => (
                          <OnboardingMembershipAutosaveForm
                            key={c.id}
                            communityId={c.id}
                            communityName={c.name}
                            config={c.onboardingForm!}
                            initialAnswers={c.metadata?.onboardingAnswers}
                          />
                        ))}
                        {communitiesWithGivesAsks.map((c) => (
                          <GivesAsksCommunitySection
                            key={c.id}
                            communityId={c.id}
                            communityName={c.name}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </SegmentedTabsContent>

            {/* Danger Zone Tab */}
            <SegmentedTabsContent value="danger" className="space-y-6">
              {deleteRequest && deleteRequest.status === 'PENDING' ? (
                <Card className="border-amber-500/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-600">
                      <Trash2 className="h-5 w-5" />
                      Delete Request Pending
                    </CardTitle>
                    <CardDescription>
                      Your account deletion request is being reviewed.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
                      <p className="text-sm text-amber-700">
                        <strong>Reason:</strong> {deleteRequest.reason}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Submitted on: {new Date(deleteRequest.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="outline" onClick={handleCancelDeleteRequest}>
                      Cancel Delete Request
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-destructive/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-destructive">
                      <Trash2 className="h-5 w-5" />
                      Delete Account
                    </CardTitle>
                    <CardDescription>Request to permanently delete your account.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                      <h4 className="font-medium text-destructive mb-2">Important</h4>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Your request will be reviewed by an admin</li>
                        <li>Account deletion takes up to 1 month after approval</li>
                        <li>All your data will be permanently deleted</li>
                        <li>You can cancel while your request is pending</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="deleteReason">Reason for leaving (optional)</Label>
                      <Input
                        id="deleteReason"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="Tell us why you're leaving..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="deleteConfirmation">
                        Type <span className="font-bold">DELETE</span> to confirm
                      </Label>
                      <Input
                        id="deleteConfirmation"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="DELETE"
                        className="max-w-xs"
                      />
                    </div>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" disabled={deleteConfirmation !== 'DELETE'}>
                          Request Account Deletion
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Your request will be sent to our team for review. You can cancel this
                            request at any time while it is pending.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteAccount}
                            disabled={deleteSaving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {deleteSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Submitting...
                              </>
                            ) : (
                              'Yes, delete my account'
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              )}
            </SegmentedTabsContent>
          </SegmentedTabs>
        </div>
      </main>
    </div>
  );
}
